import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BASE_URL } from "../utils/config";
import { getAuthHeaders } from "../utils/auth";
import "../styles/AddEditTour.css";

const AddEditTour = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: "",
    city: "",
    address: "",
    distance: "",
    photo: "",
    desc: "",
    price: "",
    maxGroupSize: "",
    featured: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchTour();
    }
  }, [id]);

  const fetchTour = async () => {
    try {
      const res = await fetch(`${BASE_URL}/tours/${id}`);
      const data = await res.json();
      if (data.success) {
        setFormData({
          title: data.data.title || "",
          city: data.data.city || "",
          address: data.data.address || "",
          distance: data.data.distance || "",
          photo: data.data.photo || "",
          desc: data.data.desc || "",
          price: data.data.price || "",
          maxGroupSize: data.data.maxGroupSize || "",
          featured: data.data.featured || false,
        });
      }
    } catch (error) {
      setError("Failed to fetch tour details");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const url = isEdit
        ? `${BASE_URL}/tours/${id}`
        : `${BASE_URL}/tours`;

      const method = isEdit ? "PUT" : "POST";
      const headers = getAuthHeaders(true);

      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({
          ...formData,
          distance: Number(formData.distance),
          price: Number(formData.price),
          maxGroupSize: Number(formData.maxGroupSize),
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSuccess(isEdit ? "Tour updated successfully!" : "Tour created successfully!");
        setTimeout(() => {
          navigate("/admin/tours");
        }, 1500);
      } else {
        setError(data.message || "Failed to save tour");
      }
    } catch (error) {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-edit-tour">
      <div className="form-container">
        <h3>{isEdit ? "Edit Tour" : "Add New Tour"}</h3>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Tour Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., Beautiful Bali Adventure"
              />
            </div>

            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                placeholder="e.g., Bali"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Address *</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              placeholder="Full address of the tour location"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Distance (km) *</label>
              <input
                type="number"
                name="distance"
                value={formData.distance}
                onChange={handleChange}
                required
                min="0"
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label>Price (USD) *</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label>Max Group Size *</label>
              <input
                type="number"
                name="maxGroupSize"
                value={formData.maxGroupSize}
                onChange={handleChange}
                required
                min="1"
                placeholder="10"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Photo URL *</label>
            <input
              type="url"
              name="photo"
              value={formData.photo}
              onChange={handleChange}
              required
              placeholder="https://example.com/image.jpg"
            />
            {formData.photo && (
              <div className="image-preview">
                <img src={formData.photo} alt="Preview" />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              name="desc"
              value={formData.desc}
              onChange={handleChange}
              required
              rows="6"
              placeholder="Write a detailed description of the tour..."
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
              />
              <span>Mark as Featured Tour</span>
            </label>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={() => navigate("/admin/tours")}
            >
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update Tour" : "Create Tour"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditTour;

